import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import { getPasswordRecoveryRedirectUrl } from '../auth/passwordRecovery';

export const PASSWORD_RESET_EMAIL_SENT_MESSAGE =
  'If an account exists for that email, a reset link has been sent.';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setError('Enter the email used for your account.');
      setSuccessMessage('');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccessMessage('');

    const { error: resetError } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
      redirectTo: getPasswordRecoveryRedirectUrl(),
    });

    if (resetError) {
      setError(resetError.message || 'Unable to send a reset link right now. Please try again.');
      setSubmitting(false);
      return;
    }

    setSuccessMessage(PASSWORD_RESET_EMAIL_SENT_MESSAGE);
    setSubmitting(false);
  };

  return (
    <Container maxWidth="xs" sx={{ mt: 8 }}>
      <Box sx={{ boxShadow: 3, p: 4, borderRadius: 2 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h5" gutterBottom>Forgot password?</Typography>
            <Typography variant="body2" color="text.secondary">
              Enter your email and we&apos;ll send you a secure link to reset your password.
            </Typography>
          </Box>

          {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}
          {error ? <Alert severity="error">{error}</Alert> : null}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              label="Email"
              fullWidth
              margin="normal"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={submitting}
            />
            <Button
              variant="contained"
              type="submit"
              fullWidth
              sx={{ mt: 2 }}
              disabled={submitting}
            >
              {submitting ? 'Sending...' : 'Send reset link'}
            </Button>
          </Box>

          <Link component={RouterLink} to="/login" underline="hover">
            Back to login
          </Link>
        </Stack>
      </Box>
    </Container>
  );
};

export default ForgotPasswordPage;
